/**
 * @fileoverview Program viewer guide — the Hans Academy content for program
 * viewers: the sponsors, school coordinators and department heads an
 * organization lets follow a program.
 *
 * The fourth audience. A viewer signs in at the organization portal but runs
 * nothing, so the organization guide (programs, staffing, billing) documents a
 * portal they mostly cannot see. Shares the searchable UI in
 * `components/GuideView`, so all four guides read identically, and its four
 * topic ids are the ones a viewer's Profile links to (`#around`, `#lessons`,
 * `#roster`, `#settings`; OrgViewerProfilePage's VIEWER_TUTORIALS).
 *
 * Same scope rule as the others: only what production ships, no plan named
 * and no price quoted. What a viewer sees is decided by the organization,
 * grant by grant, so this guide never promises a section: it says what each
 * one shows when it has been shared.
 *
 * Screenshots live in `public/academy/` (the `viewer-*` set) and are captured
 * from the real viewer portal by `scripts/capture-academy.mjs`.
 */

import { Rocket, BookOpen, Users, Settings, HelpCircle } from "lucide-react";
import GuideView from "@/components/GuideView";
import { supportEmail } from "@/lib/companyBrand";

/** Shorthand for an image in `public/academy`. Mirrors GuidePage's helper. */
const shot = (file, alt, caption) => ({
  src: `/academy/${file}`,
  alt,
  caption,
  kind: file.endsWith(".gif") ? "gif" : "image",
});

export const viewerGuideCategories = [
  {
    id: "around",
    icon: Rocket,
    title: "Find Your Way Around",
    sections: [
      {
        id: "what-is-a-viewer",
        title: "What a program viewer is",
        content:
          "An organization that runs music programs can let someone follow one without running it: a school's arts coordinator, a sponsor, a department head. That is a program viewer. You see the programs the organization shared with you, and in each one only what it chose to share. Nothing you do changes how the program is taught.",
        media: shot(
          "viewer-overview.png",
          "A program viewer's Overview: the organization's logo beside the program, tiles for sessions, students and the week ahead, and each class with its schedule",
          "A program's Overview"
        ),
      },
      {
        id: "signing-in",
        title: "Signing in",
        steps: [
          "Open your invitation and choose your password. It comes as an email from Hans, or as a link the organization sends you itself, and works for 30 days",
          "After that, sign in to Hans under Organization with that email address: administrators and viewers share that sign-in, and yours opens on your programs",
          "If the link has expired, use Forgot password on the sign-in page to get a new one",
        ],
      },
      {
        id: "your-programs",
        title: "Your programs",
        content:
          "With one program shared with you, signing in opens it. With several, My programs lists them, each with what it shares, and opens the one you choose. The organization's logo and name sit in the corner and beside each program.",
      },
      {
        id: "tabs",
        title: "The tiles and the tabs",
        content:
          "A program opens on its Overview. Three tiles sit above the tabs when the program's numbers are shared with you: sessions held so far and still to come, students enrolled, and the sessions in the week ahead. Each one opens the tab behind it.",
        items: [
          { label: "Overview", desc: "Each class, when and where it meets and who teaches it, with the instructors and the program's details" },
          { label: "Lessons", desc: "Every session, held and to come, in a table you can sort and export" },
          { label: "Students", desc: "The roster, and uploading one" },
          { label: "Calendar", desc: "The program's class meetings, a week at a time" },
          { label: "Settings", desc: "Your emails, who to ask about the program, and leaving it" },
        ],
        note: "Every viewer sees a program's classes and calendar. Lessons and Students appear when the organization has shared something they can show.",
      },
    ],
  },
  {
    id: "lessons",
    icon: BookOpen,
    title: "Lessons and Attendance",
    sections: [
      {
        id: "lessons-tab",
        title: "The Lessons tab",
        content:
          "Every session of the program in one table: its date and time, the class and who taught it. A substitute is named and marked. Held, Upcoming and All choose which sessions are listed; the class and instructor menus narrow them further. Select any column heading to sort by it, and again to reverse it.",
        media: shot(
          "viewer-lessons.png",
          "The Lessons tab: sessions by date with their class, instructor, attendance and lesson notes, one opened to its summary",
          "Lessons"
        ),
      },
      {
        id: "lesson-notes",
        title: "Reading a lesson's notes",
        content:
          "When lesson notes are shared with you, a lesson the instructor wrote up shows its title. Select the row to read the summary the instructor shared with families. The Overview lists the latest three.",
        note: "Notes from a lesson that taught a student under 13, or a student whose age group isn't set, are never shown to a viewer: those go to parents only. The tab says how many were held back, never whose.",
      },
      {
        id: "attendance",
        title: "Attendance",
        content:
          "When attendance is shared with you, it shows in three places: each session's count in Lessons, each student's in Students, and each class's day-by-day grid on the Overview. The grids start closed; open a class's Attendance line to see who was there on each of its latest eight days.",
      },
      {
        id: "exporting",
        title: "Exporting",
        content:
          "Export lessons on the Lessons tab, and Export students on the Students tab, download what the tab lists as a CSV file, with only what has been shared with you. The organization's activity record notes each export.",
      },
    ],
  },
  {
    id: "roster",
    icon: Users,
    title: "Students and Rosters",
    sections: [
      {
        id: "students-tab",
        title: "The Students tab",
        content:
          "When the roster is shared with you, the Students tab lists everyone on the program's classes: their name, their class and their recording consent, with their attendance when that is shared too. Contact details and instructors' private notes are never shown.",
        media: shot(
          "viewer-students.png",
          "The Students tab: each student with their class, attendance and recording consent, with Upload a roster and Export students",
          "Students"
        ),
      },
      {
        id: "recording-consent",
        title: "What recording consent means",
        content:
          "Instructors can record a lesson so Hans can write its notes. A family decides whether that may happen, and the tag beside each student shows where that stands: given, asked and waiting for an answer, withdrawn, or none.",
      },
      {
        id: "uploading",
        title: "Uploading a class roster",
        steps: [
          "Open the Students tab and select Upload a roster, choosing the class if the program has several",
          "Paste the list, or use Choose a file for a CSV: one student per line, first name, last name, and an email if you have one",
          "Select Check the list: you see who will be added and who is already on the roster before anything is saved",
          "Select Add these to put them on the class",
        ],
        note: "Uploaded students join with no recording consent. Their families are asked separately, so nothing about an upload lets a lesson be recorded.",
      },
    ],
  },
  {
    id: "settings",
    icon: Settings,
    title: "Your Settings and Profile",
    sections: [
      {
        id: "emails",
        title: "Your program emails",
        content:
          "An organization can send you a weekly recap of what was taught, and a week-ahead of what is scheduled, on the day and time it chose. A program's Settings tab lists the ones you were given, each with a switch to turn it off or back on.",
      },
      {
        id: "contact",
        title: "Questions about a program",
        content:
          "The program's Settings tab names the people to ask: its contacts, or the organization's administrators. Email them from there. Only the organization can change what you see, so ask them for more, or less.",
      },
      {
        id: "leaving",
        title: "Leaving a program",
        content:
          "Leave program, at the bottom of a program's Settings tab, removes it from what you can see and stops its emails straight away. If it is the only program shared with you, your viewer account closes and you're signed out. The organization can always add you again.",
      },
      {
        id: "profile",
        title: "Your Profile",
        content: "Profile, at the bottom of the menu, holds the rest:",
        items: [
          { label: "About Hans", desc: "What Hans is, and a link to share it with a teacher, a school or a music program" },
          { label: "Your account", desc: "Your name and email, and changing your password" },
          { label: "Appearance", desc: "Light or dark, saved on that device" },
          { label: "Hans Academy", desc: "This guide" },
          { label: "Help", desc: "Contacting Hans support" },
        ],
        media: shot(
          "viewer-profile.png",
          "A program viewer's Profile: About Hans with Share Hans, the account and password, appearance, this guide and support",
          "Profile"
        ),
      },
    ],
  },
  {
    id: "faq",
    icon: HelpCircle,
    title: "FAQ",
    sections: [
      {
        id: "faq-missing-tab",
        title: "I can't see Lessons, Students or the tiles",
        content:
          "Each is shared separately by the organization. If a program shows only its Overview, Calendar and Settings, nothing more has been shared with you yet. Its Settings tab lists exactly what has.",
      },
      {
        id: "faq-calendar",
        title: "A class is missing from the calendar",
        content:
          "The calendar shows a program's classes as they are scheduled. A program of private lessons is booked directly with each family, so its sessions aren't on it. If the calendar says it is incomplete, refresh in a moment.",
      },
      {
        id: "faq-contact",
        title: "How do I get help?",
        content: `For anything about a program, ask the organization: its contacts are on the program's Settings tab. For anything about Hans itself, email ${supportEmail()} and a person will answer.`,
      },
    ],
  },
];

const ViewerGuidePage = () => (
  <GuideView
    categories={viewerGuideCategories}
    title="Hans Academy"
    intro="Your guide to following a program on Hans: its classes and calendar, its lessons and students, and your own settings. Search below or browse by topic."
  />
);

export default ViewerGuidePage;
